import { Injectable } from '@angular/core';
import { environment } from '@app/../environments/environment';
@Injectable({
  providedIn: 'root'
})
export class IndexedDBService {
  dbName = environment.INDEXED_DB_NAME;
  dbVersion = Math.max(environment.INDEXED_DB_Version || 1, 5);
  private db!: IDBDatabase;
  private dbOpenPromise: Promise<any>;

  constructor() {
    this.initDB();
  }

  /** Opens or reuses the environment-scoped IndexedDB connection. */
  initDB(): Promise<any> {
    if (this.db) {
      return Promise.resolve(true);
    }
    if (this.dbOpenPromise) {
      return this.dbOpenPromise;
    }

    this.dbOpenPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event: any) => {
        this.db = event.target.result;
        let stores = [
          'AttributeType', 
          'CollectionEntry', 
          'Collection', 
          'DependencyMap', 
          'TemplateCreator', 
          'TransformationTemplate', 
          'AttributeData', 
          'Meta',
          'SyncMetadata'
        ];
        stores.forEach(store => {
          this.createStore(store);
        });
      };

      request.onsuccess = (event: any) => {
        this.db = event.target.result;
        this.db.onversionchange = () => {
          this.db.close();
          this.db = undefined;
          this.dbOpenPromise = null;
        };
        resolve(true);
      };

      request.onerror = (event: any) => {
        this.dbOpenPromise = null;
        reject(false);
      };

      request.onblocked = () => {
        console.warn('IndexedDB open is waiting for another tab to close its older database connection.');
      };
    });

    return this.dbOpenPromise;
  }

  createStore(name: string) {
    if (!this.db.objectStoreNames.contains(name)) {
      let store = this.db.createObjectStore(name, { keyPath: 'Id', autoIncrement: false });
      store.createIndex('Id', 'Id', { unique: true });
    }
  }

  add(storeName: string, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.add(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  upsert(storeName: string, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async upsertAll(storeName: string, data: any[]): Promise<any[]> {
    if (!this.db) {
      await this.initDB();
    }

    if (!data || data.length === 0) {
      return [];
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);

      tx.oncomplete = () => resolve(data);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);

      for (let value of data) {
        store.put(value);
      }
    });
  }

  getAll(storeName: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      if (!this.db) {
        await this.initDB();
      }
      const tx = this.db.transaction([storeName], 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  delete(storeName: string, key: IDBValidKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /** Returns a record by primary key without scanning the object store. */
  async get(storeName: string, key: IDBValidKey): Promise<any> {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readonly');
      const request = tx.objectStore(storeName).get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /** Returns the number of records in an object store using IDBObjectStore.count(). */
  async count(storeName: string): Promise<number> {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db.objectStoreNames.contains(storeName)) {
      return 0;
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readonly');
      const request = tx.objectStore(storeName).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearStore(storeName: string): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  clearAllStores(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        return reject("DB not initialized");
      }

      const storeNames = Array.from(this.db.objectStoreNames);
      if (storeNames.length === 0) {
        return resolve();
      }

      const tx = this.db.transaction(storeNames, 'readwrite');

      tx.oncomplete = () => {
        console.log("All stores cleared");
        resolve();
      };
      tx.onerror = () => {
        console.error("Error clearing stores", tx.error);
        reject(tx.error);
      };

      storeNames.forEach((storeName: string) => {
        const store = tx.objectStore(storeName);
        store.clear();
      });
    });
  }

  getByIndex(storeName: string, indexName: string, value: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readonly');
      const store = tx.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.get(value);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
